/* eslint-disable react/jsx-props-no-spreading */
import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useState, useCallback } from 'react';

import { useResizeObserver } from '../../hooks/useObserver';
import * as AppPropTypes from '../../lib/PropTypes';

import { useModal } from '../../contexts/ModalsContext';
import Button from '../buttons/Button';
import Image from '../partials/Image';

import styles from '../../../styles/blocks/gallery.module.scss';

const propTypes = {
    layout: PropTypes.oneOf(['strip', 'mosaic']).isRequired,
    images: PropTypes.arrayOf(AppPropTypes.image).isRequired,
    isContainer: PropTypes.bool,
    className: PropTypes.string,
    containerClassName: PropTypes.string,
};

const defaultProps = {
    className: null,
    isContainer: false,
    containerClassName: null,
};

const GalleryBlock = ({ layout, images, isContainer, className, containerClassName }) => {
    const { setModal } = useModal();
    const setGalleryModal = useCallback(
        (e, image) => {
            e.preventDefault();
            setModal({
                type: 'gallery',
                images,
                image,
            });
        },
        [setModal, images],
    );

    const [visible, setVisible] = useState(layout !== 'strip');
    const {
        ref,
        entry: { contentRect = null },
    } = useResizeObserver();

    const { width = 0 } = contentRect || {};

    const numberOfIteration = useMemo(() => {
        if (layout === 'mosaic') {
            return 1;
        }
        return Math.ceil(width / (images.length * 150)) + 1;
    }, [images, layout, width]);

    const animationDuration = useMemo(() => 20 * ((images.length * 250) / width), [width, images]);

    useEffect(() => {
        if (layout === 'strip') {
            setVisible(false);
            setTimeout(() => setVisible(true), 10);
        }
    }, [width]);

    return (
        <div
            className={classNames(styles.container, styles[`${layout}Layout`], {
                [className]: className !== null,
                [styles.visible]: visible,
            })}
            ref={ref}
        >
            <div
                className={classNames([
                    'blockGallery',
                    styles.inner,
                    {
                        [styles.isContainer]: isContainer,
                        [containerClassName]: isContainer && containerClassName !== null,
                    },
                ])}
            >
                {[...Array(numberOfIteration)].map((_, iterationI) => (
                    <div
                        className={styles.slideshowContainer}
                        style={{ animationDuration: `${animationDuration}s` }}
                        key={`iteration-${iterationI + 1}`}
                    >
                        {(images || []).map((image, index) =>
                            layout === 'mosaic' ? (
                                <Button
                                    onClick={(e) => setGalleryModal(e, image)}
                                    className={styles.imageButton}
                                    key={`image-${index + 1}`}
                                >
                                    <Image
                                        media={image}
                                        className={styles.imageFrame}
                                        imageClassName={styles.image}
                                        cover
                                        fit
                                    />
                                </Button>
                            ) : (
                                <Image
                                    media={image}
                                    key={`image-${index + 1}`}
                                    className={styles.imageFrame}
                                    imageClassName={styles.image}
                                    cover
                                    fit
                                />
                            ),
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
};

GalleryBlock.propTypes = propTypes;
GalleryBlock.defaultProps = defaultProps;

export default GalleryBlock;
